import { useMemo, useCallback } from "react";
import {
  redirect,
  createFileRoute,
  Outlet,
  useNavigate,
  useLocation,
  useRouteContext,
} from "@tanstack/react-router";
import { BaseLayout } from "@flukecorp-emu/common-ui";
import {
  DashboardOutlined,
  CategoryOutlined,
  AccountTreeOutlined,
  PrecisionManufacturing,
} from "@mui/icons-material";
import CustomSnackbarProvider from "../components/CustomSnackbarProvider";
import { logout } from "../services/auth";
import { ConfirmDialogProvider } from "../context/ConfirmDialogProvider";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    if (!context?.auth?.isAuthenticated) {
      return redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(() => {
    logout();
  }, []);

  const profileMenuItems = useMemo(
    () => ({
      logout: {
        title: "Logout",
        onClick: handleLogout,
      },
    }),
    [handleLogout],
  );

  const navList = useMemo(
    () => [
      {
        title: "Dashboard",
        icon: <DashboardOutlined />,
        onClick: () => {
          navigate({ to: "/dashboard" });
        },
        selected:
          location.pathname === "/" ||
          location.pathname?.startsWith("/dashboard"),
      },
      {
        title: "Catalog",
        icon: <CategoryOutlined />,
        onClick: () => {
          navigate({ to: "/catalog" });
        },
        selected: location.pathname?.startsWith("/catalog"),
      },
      {
        title: "Taxonomy",
        icon: <AccountTreeOutlined />,
        onClick: () => {
          navigate({ to: "/taxonomy" });
        },
        selected: location.pathname?.startsWith("/taxonomy"),
      },
      {
        title: "OptiCal",
        icon: <PrecisionManufacturing />,
        onClick: () => {
          navigate({ to: "/optical" });
        },
        selected: location.pathname?.startsWith("/optical"),
      },
    ],
    [navigate, location.pathname],
  );

  const { auth } = useRouteContext({ from: "/_authenticated" });
  const userName = auth?.user?.name || "Admin User";
  const userEmail = auth?.user?.email || "";

  return (
    <CustomSnackbarProvider>
      <ConfirmDialogProvider>
        <BaseLayout
          hideAppSwitcher={true}
          navList={navList}
          profileMenuItems={profileMenuItems}
          hideProfile={true}
          hideSearch={true}
          hideHelp={true}
          userData={{
            title: userName,
            subtitle: userEmail,
          }}
          productName="Fluke Admin"
        >
          <Outlet />
        </BaseLayout>
      </ConfirmDialogProvider>
    </CustomSnackbarProvider>
  );
}
